import type {
  Sale,
  SaleDocument,
  SaleHistoryEntry,
  SalePartner,
} from "@tsi/domain";

const TODAY = new Date("2026-05-09T18:00:00Z");

function hoursAgo(h: number): string {
  const dt = new Date(TODAY);
  dt.setHours(dt.getHours() - h);
  return dt.toISOString();
}
function daysAgo(d: number): string {
  return hoursAgo(d * 24).slice(0, 10);
}
function daysFromNow(d: number): string {
  const dt = new Date(TODAY);
  dt.setDate(dt.getDate() + d);
  return dt.toISOString().slice(0, 10);
}
function h(
  hoursAgoFromNow: number,
  type: SaleHistoryEntry["type"],
  message: string,
  author?: string,
): SaleHistoryEntry {
  return { at: hoursAgo(hoursAgoFromNow), type, message, author };
}

// Parceiros recorrentes — ids alinhados aos partnerships de mockPartners
const CONEX: SalePartner = {
  id: "p1",
  name: "Conex Consórcio",
  commissionPct: 0.03,
  commissionPaid: false,
};
const TORTATO: SalePartner = {
  id: "p2",
  name: "Tortato Corporações",
  commissionPct: 0.025,
  commissionPaid: false,
};
const SERVOPA: SalePartner = {
  id: "p3",
  name: "Servopa Private",
  commissionPct: 0.03,
  commissionPaid: false,
};
const INTELLI: SalePartner = {
  id: "p4",
  name: "IntelliVirtus",
  commissionPct: 0.025,
  commissionPaid: false,
};

/** Cria a lista de docs com alguns recebidos e outros pendentes */
function docs(
  spec: Partial<
    Record<
      "rg" | "cpf" | "income" | "address" | "deed",
      "received" | "pending" | "issue"
    >
  >,
): SaleDocument[] {
  const map: Record<string, string> = {
    rg: "RG",
    cpf: "CPF",
    income: "Comprovante de renda",
    address: "Comprovante de residência",
    deed: "Escritura do imóvel",
  };
  return (Object.keys(map) as (keyof typeof map)[]).map((key) => ({
    key,
    name: map[key],
    status: spec[key as keyof typeof spec] ?? "pending",
  }));
}

export const mockSales: Sale[] = [
  // ────────────────── COLUNA 1: RESERVADA ──────────────────
  {
    id: "s1",
    cardRef: 7222,
    assetType: "real_estate",
    customer: {
      id: "c2",
      name: "Maria Oliveira",
      cpf: "234.567.890-12",
      phone: "(41) 91234-5678",
      email: "maria.oliveira@email.com",
    },
    cota: {
      credit: 111_372,
      installments: 232,
      monthlyPayment: 594.48,
      transferTax: 1122.51,
    },
    totalValue: 44_549,
    signal: { amount: 13_365, paid: true, paidAt: hoursAgo(8), method: "pix" },
    residual: { amount: 31_184, paid: false },
    tsiCost: 38_500,
    partner: CONEX,
    phase: "reserved",
    phaseEnteredAt: hoursAgo(8),
    entryDate: daysAgo(0),
    deadline: daysFromNow(2),
    documents: docs({}),
    creditStatus: "pending",
    history: [
      h(8, "reserved", "Cota reservada via Conex Consórcio", "Gabriel"),
      h(8, "signal_received", "Sinal de R$ 13.365 recebido via PIX", "Sistema"),
    ],
    notes: "Cliente confirmou envio de docs até quarta.",
  },
  {
    id: "s2",
    cardRef: 7341,
    assetType: "real_estate",
    customer: {
      id: "c5",
      name: "Carlos Lima",
      cpf: "567.890.123-45",
      phone: "(41) 94444-5678",
      email: "carlos.lima@email.com",
    },
    cota: {
      credit: 56_489,
      installments: 208,
      monthlyPayment: 350.94,
      transferTax: 919.66,
    },
    totalValue: 22_595,
    signal: { amount: 6_780, paid: true, paidAt: hoursAgo(20), method: "pix" },
    residual: { amount: 15_815, paid: false },
    tsiCost: 18_500,
    phase: "reserved",
    phaseEnteredAt: hoursAgo(20),
    entryDate: daysAgo(0),
    deadline: daysFromNow(2),
    documents: docs({}),
    creditStatus: "pending",
    history: [
      h(20, "reserved", "Cota reservada (walk-in)", "Isaac"),
      h(20, "signal_received", "Sinal R$ 6.780 via PIX", "Sistema"),
    ],
  },
  {
    id: "s3",
    cardRef: 8147,
    assetType: "auto",
    customer: {
      id: "c10",
      name: "Patrícia Dias",
      cpf: "012.345.678-90",
      phone: "(41) 99119-4444",
      email: "patricia.dias@email.com",
    },
    cota: {
      credit: 45_200,
      installments: 72,
      monthlyPayment: 715.8,
      transferTax: 612.5,
    },
    totalValue: 13_560,
    signal: {
      amount: 4_068,
      paid: true,
      paidAt: hoursAgo(40),
      method: "transfer",
    },
    residual: { amount: 9_492, paid: false },
    tsiCost: 11_500,
    partner: TORTATO,
    phase: "reserved",
    phaseEnteredAt: hoursAgo(40), // > 36h → laranja
    entryDate: daysAgo(2),
    deadline: daysFromNow(0),
    documents: docs({}),
    creditStatus: "pending",
    history: [
      h(40, "reserved", "Reservada via Tortato Corporações", "Amélia"),
      h(40, "signal_received", "Sinal R$ 4.068 por transferência", "Sistema"),
    ],
    notes: "Lembrete enviado — falta envio dos docs.",
  },
  {
    id: "s4",
    cardRef: 7359,
    assetType: "real_estate",
    customer: {
      id: "c12",
      name: "Ribeiro Imóveis LTDA",
      cpf: "22.333.444/0001-55",
      phone: "(41) 97777-6666",
      email: "compras@ribeiroimoveis.com.br",
    },
    cota: {
      credit: 64_398,
      installments: 228,
      monthlyPayment: 388.04,
      transferTax: 919.66,
    },
    totalValue: 25_759,
    signal: { amount: 7_728, paid: true, paidAt: hoursAgo(56), method: "pix" },
    residual: { amount: 18_031, paid: false },
    tsiCost: 21_500,
    partner: SERVOPA,
    phase: "reserved",
    phaseEnteredAt: hoursAgo(56), // > 48h → vermelho (perde reserva se não enviar docs)
    entryDate: daysAgo(2),
    deadline: daysFromNow(-1),
    documents: docs({}),
    creditStatus: "pending",
    history: [
      h(56, "reserved", "Reservada via Servopa Private", "Gabriel"),
      h(56, "signal_received", "Sinal R$ 7.728 via PIX", "Sistema"),
      h(
        8,
        "comment",
        "Cliente disse que envia docs até hoje à noite",
        "Andressa",
      ),
    ],
  },

  // ──────────────── COLUNA 2: DOCUMENTAÇÃO ────────────────
  {
    id: "s5",
    cardRef: 7227,
    assetType: "real_estate",
    customer: {
      id: "c1",
      name: "João Silva",
      cpf: "123.456.789-01",
      phone: "(41) 98765-4321",
      email: "joao.silva@email.com",
    },
    cota: {
      credit: 67_224,
      installments: 224,
      monthlyPayment: 399.43,
      transferTax: 1092.09,
    },
    totalValue: 26_890,
    signal: { amount: 8_067, paid: true, paidAt: daysAgo(5), method: "pix" },
    residual: { amount: 18_823, paid: false },
    tsiCost: 22_500,
    partner: CONEX,
    phase: "documentation",
    phaseEnteredAt: hoursAgo(36),
    entryDate: daysAgo(5),
    deadline: daysFromNow(1),
    documents: docs({
      rg: "received",
      cpf: "received",
      address: "received",
      income: "pending",
      deed: "pending",
    }),
    creditStatus: "pending",
    history: [
      h(5 * 24, "reserved", "Cota reservada", "Gabriel"),
      h(5 * 24, "signal_received", "Sinal R$ 8.067 via PIX", "Sistema"),
      h(
        36,
        "doc_received",
        "RG, CPF e Comprovante de residência recebidos",
        "Andressa",
      ),
      h(2, "comment", "Aguardando comprovante de renda", "Andressa"),
    ],
    notes: "Cliente informou que envia comprovante de renda hoje.",
  },
  {
    id: "s6",
    cardRef: 7273,
    assetType: "real_estate",
    customer: {
      id: "c6",
      name: "Juliana Mendes",
      cpf: "678.901.234-56",
      phone: "(41) 93333-9876",
      email: "juliana.mendes@email.com",
    },
    cota: {
      credit: 66_145,
      installments: 208,
      monthlyPayment: 439.89,
      transferTax: 1033.12,
    },
    totalValue: 26_458,
    signal: { amount: 7_937, paid: true, paidAt: daysAgo(7), method: "pix" },
    residual: { amount: 18_521, paid: false },
    tsiCost: 22_000,
    partner: SERVOPA,
    phase: "documentation",
    phaseEnteredAt: hoursAgo(80), // > 72h → vermelho
    entryDate: daysAgo(7),
    deadline: daysAgo(1),
    documents: docs({
      rg: "received",
      cpf: "received",
      address: "received",
      income: "issue",
      deed: "pending",
    }),
    creditStatus: "pending",
    history: [
      h(7 * 24, "reserved", "Reservada", "Isaac"),
      h(7 * 24, "signal_received", "Sinal recebido via PIX", "Sistema"),
      h(
        4 * 24,
        "doc_received",
        "RG, CPF e Comprovante de residência recebidos",
        "Andressa",
      ),
      h(
        80,
        "doc_issue",
        "Comprovante de renda ilegível — solicitado novo",
        "Andressa",
      ),
    ],
    notes: "Aguardando reenvio do comprovante de renda.",
  },
  {
    id: "s7",
    cardRef: 8163,
    assetType: "auto",
    customer: {
      id: "c9",
      name: "Fernando Rocha",
      cpf: "901.234.567-89",
      phone: "(41) 90000-3333",
      email: "fernando.rocha@email.com",
    },
    cota: {
      credit: 52_400,
      installments: 84,
      monthlyPayment: 712.3,
      transferTax: 689.4,
    },
    totalValue: 15_720,
    signal: { amount: 4_716, paid: true, paidAt: daysAgo(3), method: "pix" },
    residual: { amount: 11_004, paid: false },
    tsiCost: 13_500,
    partner: TORTATO,
    phase: "documentation",
    phaseEnteredAt: hoursAgo(20),
    entryDate: daysAgo(3),
    deadline: daysFromNow(2),
    documents: docs({
      rg: "received",
      cpf: "received",
      income: "received",
      address: "received",
    }),
    creditStatus: "pending",
    history: [
      h(3 * 24, "reserved", "Reservada via Tortato Corporações", "Amélia"),
      h(3 * 24, "signal_received", "Sinal R$ 4.716 via PIX", "Sistema"),
      h(
        20,
        "docs_complete",
        "Todos os documentos OK — pronta para análise",
        "Andressa",
      ),
    ],
  },
  {
    id: "s8",
    cardRef: 7203,
    assetType: "real_estate",
    customer: {
      id: "c4",
      name: "Ana Costa",
      cpf: "456.789.012-34",
      phone: "(41) 95555-1234",
      email: "ana.costa@email.com",
    },
    cota: {
      credit: 111_372,
      installments: 233,
      monthlyPayment: 600.77,
      transferTax: 1122.51,
    },
    totalValue: 44_549,
    signal: { amount: 13_365, paid: true, paidAt: daysAgo(8), method: "pix" },
    residual: { amount: 31_184, paid: false },
    tsiCost: 38_000,
    phase: "documentation",
    phaseEnteredAt: hoursAgo(60),
    entryDate: daysAgo(8),
    deadline: daysFromNow(0),
    documents: docs({
      rg: "received",
      cpf: "pending",
      income: "pending",
      address: "received",
    }),
    creditStatus: "pending",
    history: [
      h(8 * 24, "reserved", "Cota reservada", "Gabriel"),
      h(8 * 24, "signal_received", "Sinal R$ 13.365 via PIX", "Sistema"),
      h(
        60,
        "doc_received",
        "RG e Comprovante de residência recebidos",
        "Andressa",
      ),
    ],
    notes: "Cliente lento no envio dos documentos.",
  },

  // ──────────────── COLUNA 3: ANÁLISE DE CRÉDITO ────────────────
  {
    id: "s9",
    cardRef: 7263,
    assetType: "real_estate",
    customer: {
      id: "c3",
      name: "Pedro Santos",
      cpf: "345.678.901-23",
      phone: "(41) 99876-5432",
      email: "pedro.santos@email.com",
    },
    cota: {
      credit: 65_462,
      installments: 228,
      monthlyPayment: 384.73,
      transferTax: 1033.12,
    },
    totalValue: 26_185,
    signal: { amount: 7_856, paid: true, paidAt: daysAgo(10), method: "pix" },
    residual: { amount: 18_329, paid: false },
    tsiCost: 22_000,
    partner: TORTATO,
    phase: "analysis",
    phaseEnteredAt: daysAgo(3),
    entryDate: daysAgo(10),
    deadline: daysFromNow(7),
    documents: docs({
      rg: "received",
      cpf: "received",
      income: "received",
      address: "received",
    }),
    creditAnalysis: {
      pipefiCardId: "PIP-98521",
      submittedAt: daysAgo(3),
      status: "in_analysis",
      lastUpdate: hoursAgo(6),
      note: "Servopa analisando comprovação de renda do cliente.",
    },
    creditStatus: "pending",
    history: [
      h(10 * 24, "reserved", "Reservada via Tortato Corporações", "Isaac"),
      h(10 * 24, "signal_received", "Sinal R$ 7.856 via PIX", "Sistema"),
      h(6 * 24, "docs_complete", "Todos os documentos validados", "Andressa"),
      h(
        3 * 24,
        "submitted_to_admin",
        "Enviado à Servopa (PIP-98521)",
        "Andressa",
      ),
    ],
  },
  {
    id: "s10",
    cardRef: 7229,
    assetType: "real_estate",
    customer: {
      id: "c7",
      name: "Roberto Alves",
      cpf: "789.012.345-67",
      phone: "(41) 92222-1111",
      email: "roberto.alves@email.com",
    },
    cota: {
      credit: 111_372,
      installments: 232,
      monthlyPayment: 596.95,
      transferTax: 1122.51,
    },
    totalValue: 44_549,
    signal: { amount: 13_365, paid: true, paidAt: daysAgo(12), method: "pix" },
    residual: { amount: 31_184, paid: false },
    tsiCost: 38_500,
    partner: CONEX,
    phase: "analysis",
    phaseEnteredAt: daysAgo(8), // > 7 dias → laranja
    entryDate: daysAgo(12),
    deadline: daysFromNow(2),
    documents: docs({
      rg: "received",
      cpf: "received",
      income: "received",
      address: "received",
    }),
    creditAnalysis: {
      pipefiCardId: "PIP-98477",
      submittedAt: daysAgo(8),
      status: "in_analysis",
      lastUpdate: daysAgo(2),
      note: "Pendente confirmação de vínculo empregatício.",
    },
    creditStatus: "pending",
    history: [
      h(12 * 24, "reserved", "Reservada", "Gabriel"),
      h(12 * 24, "signal_received", "Sinal R$ 13.365", "Sistema"),
      h(9 * 24, "docs_complete", "Documentos completos", "Andressa"),
      h(
        8 * 24,
        "submitted_to_admin",
        "Enviado à Servopa (PIP-98477)",
        "Andressa",
      ),
    ],
    notes: "Já cobramos a administradora.",
  },
  {
    id: "s11",
    cardRef: 8189,
    assetType: "auto",
    customer: {
      id: "c14",
      name: "Renata Castro",
      cpf: "444.555.666-77",
      phone: "(41) 95555-8888",
      email: "renata.castro@email.com",
    },
    cota: {
      credit: 58_900,
      installments: 84,
      monthlyPayment: 802.65,
      transferTax: 745.8,
    },
    totalValue: 17_670,
    signal: { amount: 5_301, paid: true, paidAt: daysAgo(11), method: "pix" },
    residual: { amount: 12_369, paid: false },
    tsiCost: 14_800,
    partner: TORTATO,
    phase: "analysis",
    phaseEnteredAt: daysAgo(11), // > 10 dias → vermelho
    entryDate: daysAgo(15),
    deadline: daysAgo(1),
    documents: docs({
      rg: "received",
      cpf: "received",
      income: "received",
      address: "received",
    }),
    creditAnalysis: {
      pipefiCardId: "PIP-98301",
      submittedAt: daysAgo(11),
      status: "in_analysis",
      lastUpdate: daysAgo(4),
      note: "Sem retorno da administradora — necessário cobrar.",
    },
    creditStatus: "pending",
    history: [
      h(15 * 24, "reserved", "Reservada via Tortato Corporações", "Amélia"),
      h(15 * 24, "signal_received", "Sinal R$ 5.301", "Sistema"),
      h(12 * 24, "docs_complete", "Documentos OK", "Andressa"),
      h(
        11 * 24,
        "submitted_to_admin",
        "Enviado à Servopa (PIP-98301)",
        "Andressa",
      ),
    ],
    notes: "Atrasada — cobrar Servopa hoje.",
  },

  // ──────────────────── COLUNA 4: APROVADO ────────────────────
  {
    id: "s12",
    cardRef: 7220,
    assetType: "real_estate",
    customer: {
      id: "c4",
      name: "Ana Costa",
      cpf: "456.789.012-34",
      phone: "(41) 95555-1234",
      email: "ana.costa@email.com",
    },
    cota: {
      credit: 104_976,
      installments: 176,
      monthlyPayment: 706.35,
      transferTax: 1033.12,
    },
    totalValue: 41_990,
    signal: { amount: 12_597, paid: true, paidAt: daysAgo(14), method: "pix" },
    residual: { amount: 29_393, paid: false },
    tsiCost: 35_000,
    phase: "approved",
    phaseEnteredAt: daysAgo(2),
    entryDate: daysAgo(14),
    deadline: daysFromNow(5),
    documents: docs({
      rg: "received",
      cpf: "received",
      income: "received",
      address: "received",
    }),
    creditAnalysis: {
      pipefiCardId: "PIP-98112",
      submittedAt: daysAgo(7),
      status: "approved",
      lastUpdate: daysAgo(2),
    },
    contractStatus: "sent",
    contractSentAt: daysAgo(2),
    creditStatus: "approved",
    history: [
      h(14 * 24, "reserved", "Cota reservada", "Isaac"),
      h(14 * 24, "signal_received", "Sinal R$ 12.597 via PIX", "Sistema"),
      h(8 * 24, "docs_complete", "Docs OK", "Andressa"),
      h(7 * 24, "submitted_to_admin", "Enviado à Servopa", "Andressa"),
      h(
        2 * 24,
        "credit_approved",
        "Crédito aprovado pela administradora",
        "Servopa",
      ),
      h(
        2 * 24,
        "contract_sent",
        "Contrato DocuSign enviado ao cliente",
        "Andressa",
      ),
    ],
  },
  {
    id: "s13",
    cardRef: 7264,
    assetType: "real_estate",
    customer: {
      id: "c8",
      name: "Beatriz Souza",
      cpf: "890.123.456-78",
      phone: "(41) 91111-2222",
      email: "beatriz.souza@email.com",
    },
    cota: {
      credit: 111_372,
      installments: 232,
      monthlyPayment: 594.48,
      transferTax: 1098.48,
    },
    totalValue: 44_549,
    signal: { amount: 13_365, paid: true, paidAt: daysAgo(15), method: "pix" },
    residual: { amount: 31_184, paid: false },
    tsiCost: 38_000,
    partner: INTELLI,
    phase: "approved",
    phaseEnteredAt: daysAgo(6),
    entryDate: daysAgo(15),
    deadline: daysFromNow(1),
    documents: docs({
      rg: "received",
      cpf: "received",
      income: "received",
      address: "received",
    }),
    creditAnalysis: {
      pipefiCardId: "PIP-97999",
      submittedAt: daysAgo(10),
      status: "approved",
    },
    contractStatus: "signed",
    contractSentAt: daysAgo(6),
    creditStatus: "approved",
    history: [
      h(15 * 24, "reserved", "Reservada via IntelliVirtus", "Gabriel"),
      h(15 * 24, "signal_received", "Sinal recebido", "Sistema"),
      h(11 * 24, "docs_complete", "Docs OK", "Andressa"),
      h(10 * 24, "submitted_to_admin", "Enviado à Servopa", "Andressa"),
      h(7 * 24, "credit_approved", "Crédito aprovado", "Servopa"),
      h(6 * 24, "contract_sent", "Contrato enviado", "Andressa"),
      h(36, "contract_signed", "Cliente assinou via DocuSign", "Cliente"),
    ],
    notes: "Aguardando pagamento residual.",
  },
  {
    id: "s14",
    cardRef: 8225,
    assetType: "auto",
    customer: {
      id: "c15",
      name: "Thiago Nunes",
      cpf: "555.666.777-88",
      phone: "(41) 94444-9999",
      email: "thiago.nunes@email.com",
    },
    cota: {
      credit: 74_900,
      installments: 100,
      monthlyPayment: 873.65,
      transferTax: 918.2,
    },
    totalValue: 22_470,
    signal: { amount: 6_741, paid: true, paidAt: daysAgo(17), method: "pix" },
    residual: { amount: 15_729, paid: false },
    tsiCost: 19_500,
    partner: TORTATO,
    phase: "approved",
    phaseEnteredAt: daysAgo(8), // > 7 dias → vermelho
    entryDate: daysAgo(17),
    deadline: daysAgo(1),
    documents: docs({
      rg: "received",
      cpf: "received",
      income: "received",
      address: "received",
    }),
    creditAnalysis: {
      pipefiCardId: "PIP-97770",
      submittedAt: daysAgo(12),
      status: "approved",
    },
    contractStatus: "sent",
    contractSentAt: daysAgo(8),
    creditStatus: "approved",
    history: [
      h(17 * 24, "reserved", "Reservada via Tortato Corporações", "Amélia"),
      h(17 * 24, "signal_received", "Sinal R$ 6.741", "Sistema"),
      h(13 * 24, "docs_complete", "Docs completos", "Andressa"),
      h(12 * 24, "submitted_to_admin", "Enviado à Servopa", "Andressa"),
      h(9 * 24, "credit_approved", "Crédito aprovado", "Servopa"),
      h(8 * 24, "contract_sent", "Contrato enviado", "Andressa"),
    ],
    notes: "Cliente atrasado para assinar — Lucca cobrando.",
  },

  // ──────────────────── COLUNA 5: FINALIZADO ───────────────────
  {
    id: "s15",
    cardRef: 7173,
    assetType: "real_estate",
    customer: {
      id: "c11",
      name: "Lucas Pereira",
      cpf: "111.222.333-44",
      phone: "(41) 98888-5555",
      email: "lucas.pereira@email.com",
    },
    cota: {
      credit: 68_093,
      installments: 208,
      monthlyPayment: 443.39,
      transferTax: 1065.77,
    },
    totalValue: 27_237,
    signal: { amount: 8_171, paid: true, paidAt: daysAgo(28), method: "pix" },
    residual: {
      amount: 19_066,
      paid: true,
      paidAt: daysAgo(15),
      method: "pix",
    },
    tsiCost: 22_500,
    partner: SERVOPA,
    phase: "completed",
    phaseEnteredAt: daysAgo(12),
    entryDate: daysAgo(28),
    documents: docs({
      rg: "received",
      cpf: "received",
      income: "received",
      address: "received",
    }),
    creditAnalysis: {
      pipefiCardId: "PIP-96321",
      submittedAt: daysAgo(22),
      status: "approved",
    },
    contractStatus: "paid",
    contractSentAt: daysAgo(18),
    siscomRegisteredAt: daysAgo(13),
    finalizedAt: daysAgo(12),
    creditStatus: "approved",
    history: [
      h(28 * 24, "reserved", "Reservada via Servopa Private", "Gabriel"),
      h(28 * 24, "signal_received", "Sinal R$ 8.171 via PIX", "Sistema"),
      h(25 * 24, "docs_complete", "Docs validados", "Andressa"),
      h(22 * 24, "submitted_to_admin", "Enviado à Servopa", "Andressa"),
      h(19 * 24, "credit_approved", "Crédito aprovado", "Servopa"),
      h(18 * 24, "contract_sent", "Contrato enviado", "Andressa"),
      h(16 * 24, "contract_signed", "Cliente assinou", "Cliente"),
      h(15 * 24, "residual_received", "Residual R$ 19.066 via PIX", "Sistema"),
      h(13 * 24, "siscom_registered", "Cota registrada no Siscom", "Lucca"),
      h(12 * 24, "completed", "Venda finalizada", "Lucca"),
    ],
  },
  {
    id: "s16",
    cardRef: 7172,
    assetType: "real_estate",
    customer: {
      id: "c12",
      name: "Camila Ribeiro",
      cpf: "222.333.444-55",
      phone: "(41) 97777-6666",
      email: "camila.ribeiro@email.com",
    },
    cota: {
      credit: 70_664,
      installments: 167,
      monthlyPayment: 513.5,
      transferTax: 1149.56,
    },
    totalValue: 28_265,
    signal: { amount: 8_480, paid: true, paidAt: daysAgo(40), method: "pix" },
    residual: {
      amount: 19_785,
      paid: true,
      paidAt: daysAgo(20),
      method: "transfer",
    },
    tsiCost: 23_500,
    partner: CONEX,
    phase: "completed",
    phaseEnteredAt: daysAgo(18),
    entryDate: daysAgo(40),
    documents: docs({
      rg: "received",
      cpf: "received",
      income: "received",
      address: "received",
    }),
    creditAnalysis: {
      pipefiCardId: "PIP-95887",
      submittedAt: daysAgo(33),
      status: "approved",
    },
    contractStatus: "paid",
    contractSentAt: daysAgo(28),
    siscomRegisteredAt: daysAgo(19),
    finalizedAt: daysAgo(18),
    creditStatus: "approved",
    history: [
      h(40 * 24, "reserved", "Reservada via Conex Consórcio", "Isaac"),
      h(40 * 24, "signal_received", "Sinal recebido", "Sistema"),
      h(35 * 24, "docs_complete", "Docs OK", "Andressa"),
      h(33 * 24, "submitted_to_admin", "Enviado à Servopa", "Andressa"),
      h(30 * 24, "credit_approved", "Crédito aprovado", "Servopa"),
      h(28 * 24, "contract_sent", "Contrato enviado", "Andressa"),
      h(25 * 24, "contract_signed", "Cliente assinou", "Cliente"),
      h(20 * 24, "residual_received", "Residual recebido", "Sistema"),
      h(19 * 24, "siscom_registered", "Cota registrada no Siscom", "Lucca"),
      h(18 * 24, "completed", "Venda finalizada", "Lucca"),
    ],
    notes: "Cliente satisfeita — pediu indicar parceiro pra próxima.",
  },
  {
    id: "s17",
    cardRef: 6873,
    assetType: "real_estate",
    customer: {
      id: "c13",
      name: "Cardoso & Filhos Construtora",
      cpf: "33.444.555/0001-66",
      phone: "(41) 96666-7777",
      email: "diretoria@cardosofilhos.com.br",
    },
    cota: {
      credit: 58_073,
      installments: 208,
      monthlyPayment: 377.34,
      transferTax: 851.26,
    },
    totalValue: 23_229,
    signal: { amount: 6_968, paid: true, paidAt: daysAgo(45), method: "pix" },
    residual: {
      amount: 16_261,
      paid: true,
      paidAt: daysAgo(28),
      method: "pix",
    },
    tsiCost: 19_300,
    partner: CONEX,
    phase: "completed",
    phaseEnteredAt: daysAgo(25),
    entryDate: daysAgo(45),
    documents: docs({
      rg: "received",
      cpf: "received",
      income: "received",
      address: "received",
    }),
    creditAnalysis: {
      pipefiCardId: "PIP-94778",
      submittedAt: daysAgo(38),
      status: "approved",
    },
    contractStatus: "paid",
    contractSentAt: daysAgo(35),
    siscomRegisteredAt: daysAgo(26),
    finalizedAt: daysAgo(25),
    creditStatus: "approved",
    history: [
      h(45 * 24, "reserved", "Reservada via Conex Consórcio", "Gabriel"),
      h(45 * 24, "signal_received", "Sinal recebido", "Sistema"),
      h(40 * 24, "docs_complete", "Docs OK", "Andressa"),
      h(38 * 24, "submitted_to_admin", "Enviado à Servopa", "Andressa"),
      h(35 * 24, "credit_approved", "Crédito aprovado", "Servopa"),
      h(35 * 24, "contract_sent", "Contrato enviado", "Andressa"),
      h(31 * 24, "contract_signed", "Cliente assinou", "Cliente"),
      h(28 * 24, "residual_received", "Residual recebido", "Sistema"),
      h(26 * 24, "siscom_registered", "Cota registrada no Siscom", "Lucca"),
      h(25 * 24, "completed", "Venda finalizada", "Lucca"),
    ],
  },
  // ─── Vendas finalizadas em 03/05 (semana atual) ───
  {
    id: "s18",
    cardRef: 6926,
    assetType: "real_estate",
    customer: {
      id: "c-new1",
      name: "Helena Vasconcelos",
      cpf: "789.456.123-00",
      phone: "(41) 98554-3322",
      email: "helena.vasconcelos@email.com",
    },
    cota: {
      credit: 55_307,
      installments: 198,
      monthlyPayment: 351.74,
      transferTax: 851.26,
    },
    totalValue: 22_122,
    signal: { amount: 6_637, paid: true, paidAt: daysAgo(20), method: "pix" },
    residual: { amount: 15_485, paid: true, paidAt: daysAgo(8), method: "pix" },
    tsiCost: 18_400,
    partner: SERVOPA,
    phase: "completed",
    phaseEnteredAt: daysAgo(6),
    entryDate: daysAgo(20),
    documents: docs({
      rg: "received",
      cpf: "received",
      income: "received",
      address: "received",
    }),
    creditAnalysis: {
      pipefiCardId: "PIP-99012",
      submittedAt: daysAgo(15),
      status: "approved",
    },
    contractStatus: "paid",
    contractSentAt: daysAgo(11),
    siscomRegisteredAt: daysAgo(7),
    finalizedAt: daysAgo(6),
    creditStatus: "approved",
    history: [
      h(20 * 24, "reserved", "Reservada via Servopa Private", "Isaac"),
      h(20 * 24, "signal_received", "Sinal R$ 6.637 via PIX", "Sistema"),
      h(17 * 24, "docs_complete", "Docs OK", "Andressa"),
      h(
        15 * 24,
        "submitted_to_admin",
        "Enviado à Servopa (PIP-99012)",
        "Andressa",
      ),
      h(12 * 24, "credit_approved", "Crédito aprovado", "Servopa"),
      h(11 * 24, "contract_sent", "Contrato enviado", "Andressa"),
      h(9 * 24, "contract_signed", "Cliente assinou", "Cliente"),
      h(8 * 24, "residual_received", "Residual recebido", "Sistema"),
      h(7 * 24, "siscom_registered", "Cota registrada no Siscom", "Lucca"),
      h(6 * 24, "completed", "Venda finalizada", "Lucca"),
    ],
  },
  {
    id: "s19",
    cardRef: 8104,
    assetType: "auto",
    customer: {
      id: "c-new2",
      name: "Diego Albuquerque",
      cpf: "456.123.789-22",
      phone: "(41) 99221-5544",
      email: "diego.albuquerque@email.com",
    },
    cota: {
      credit: 32_500,
      installments: 60,
      monthlyPayment: 612.4,
      transferTax: 478.5,
    },
    totalValue: 9_750,
    signal: { amount: 2_925, paid: true, paidAt: daysAgo(18), method: "pix" },
    residual: { amount: 6_825, paid: true, paidAt: daysAgo(7), method: "pix" },
    tsiCost: 8_200,
    partner: TORTATO,
    phase: "completed",
    phaseEnteredAt: daysAgo(6),
    entryDate: daysAgo(18),
    documents: docs({
      rg: "received",
      cpf: "received",
      income: "received",
      address: "received",
    }),
    creditAnalysis: {
      pipefiCardId: "PIP-99077",
      submittedAt: daysAgo(13),
      status: "approved",
    },
    contractStatus: "paid",
    contractSentAt: daysAgo(10),
    siscomRegisteredAt: daysAgo(7),
    finalizedAt: daysAgo(6),
    creditStatus: "approved",
    history: [
      h(18 * 24, "reserved", "Reservada via Tortato Corporações", "Amélia"),
      h(18 * 24, "signal_received", "Sinal R$ 2.925 via PIX", "Sistema"),
      h(14 * 24, "docs_complete", "Docs OK", "Andressa"),
      h(
        13 * 24,
        "submitted_to_admin",
        "Enviado à Servopa (PIP-99077)",
        "Andressa",
      ),
      h(11 * 24, "credit_approved", "Crédito aprovado", "Servopa"),
      h(10 * 24, "contract_sent", "Contrato enviado", "Andressa"),
      h(8 * 24, "contract_signed", "Cliente assinou", "Cliente"),
      h(7 * 24, "residual_received", "Residual recebido", "Sistema"),
      h(7 * 24, "siscom_registered", "Cota registrada no Siscom", "Lucca"),
      h(6 * 24, "completed", "Venda finalizada", "Lucca"),
    ],
  },
];

export const SALE_PARTNERS_LIST: string[] = Array.from(
  new Set(
    mockSales
      .map((s) => s.partner?.name)
      .filter((n): n is string => Boolean(n)),
  ),
).sort();
